import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  TextField,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Autocomplete,
  Box,
  AppBar,
  Toolbar,
  Avatar,
  CircularProgress,
  IconButton,
  Fab,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DeleteIcon from '@mui/icons-material/Delete';
import { handleError, fetchRegions, fetchFiscalTypes, validateNonEmpty, validateEmail, removeNullsAndEmptys, isEditing } from '../../utils/lib';
import axios from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../providers/AuthProvider';
import CheckIcon from "@mui/icons-material/Check";
import { useConfig } from '../../providers/ConfigProvider';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { uploadPhotoAndThumbnail } from '../../utils/fileUploadHelper';
import useVetStore from '../../store/vetStore';
import { v4 as uuidv4 } from 'uuid';
import InlinePhotoCapture from '../../components/InlinePhotoCapture';
import { useTranslation } from 'react-i18next';

/** EE. UU.: imperial + AM/PM; resto: métrico + 24 h (al elegir región en el formulario). */
const isUnitedStatesRegion = (region) => {
  if (!region) return false;
  const cc = region.country_code;
  if (cc != null && String(cc).trim().toUpperCase() === 'US') return true;
  const country = (region.country || '').trim().toLowerCase();
  return country === 'united states' || country === 'estados unidos';
};

//no modificar para usar edippage. Recordar que se muestra
//en proceso de login
const EditVet = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {updateVet: updateVetFromLocation, isBranch} = location.state || {};
  const {reloadConfig} = useConfig();
  const [searchParams] = useSearchParams();
  const isInitCreate = searchParams.get("isInitCreate"); 
  const preAccessToken = searchParams.get("pre_access_token");
  const [isLoading, setIsLoading] = useState(false);
  const {login} = useAuth();
  const navigate = useNavigate();
  const snackbar = useSnackbar();

  const { 
    vet, 
    setVet, 
    updateVet
  } = useVetStore();

  // Ensure all checkbox values are booleans
  const ensureBoolean = (value) => {
    if (value === undefined || value === null) return false;
    return Boolean(value);
  };

  const [regions, setRegions] = useState([]);
  const [sellPoints, setSellPoints] = useState([]);
  const [fiscalTypes, setFiscalTypes] = useState([]);

  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({ original: null, avatar: null });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    name: useRef(null),
    region: useRef(null),
    email: useRef(null),
  });
 
  const ownerNamingOptions = [
    { label: t('edit.clientOption'), value: 'CLIENT' },
    { label: t('edit.ownerOption'), value: 'LANDLORD' },
    { label: t('edit.proprietorOption'), value: 'OWNER' },
    { label: t('edit.tutorOption'), value: 'TUTOR' }
  ];

  const petNamingOptions = [
    { label: t('edit.animalOption'), value: 'ANIMAL' },
    { label: t('edit.petOption'), value: 'PET' },
    { label: t('edit.patientOption'), value: 'PATIENT' }
  ];

  useEffect(() => {
    // Inicializar el vet desde location.state si existe
    if (updateVetFromLocation) {
      setVet({
        ...updateVetFromLocation,
        mobile_services: ensureBoolean(updateVetFromLocation.mobile_services),
        email_messaging: ensureBoolean(updateVetFromLocation.email_messaging),
        sells_planning_activity: ensureBoolean(updateVetFromLocation.sells_planning_activity),
        sells_save_p1: ensureBoolean(updateVetFromLocation.sells_save_p1),
        sells_accept_suggested: ensureBoolean(updateVetFromLocation.sells_accept_suggested),
        sells_lock_price: ensureBoolean(updateVetFromLocation.sells_lock_price),
      });
    }

    const fetchRegions_ = async () => {
      setIsLoading(true);
      const regions = await fetchRegions();
      setRegions(regions);
      setIsLoading(false);
    }    

    fetchRegions_();

    if (updateVetFromLocation) {
      fetchFiscalTypes_(updateVetFromLocation.region.country);
    }
      
    //in create, sell point doesnt exists
    //implement
    // if (isUpdate) {
    //   setDefaultSellPoint();
    // }

    refs.name.current.focus();
  }, []);

  const fetchFiscalTypes_ = async (country) => {
    const fiscalTypes = await fetchFiscalTypes(country);
    setFiscalTypes(fiscalTypes);
  }


  // Función para manejar la subida de imágenes
  const handlePhotoAndThumbUpload = async (originalFile) => {
    try {
      setIsUploadingImage(true);

      const result = await uploadPhotoAndThumbnail({
        originalFile,
        tableName: 'vets',
        onError: (error) => {
          snackbar(t('common.errorUploadingImages') + error);
        }
      });

      // Actualizar estado con las imágenes subidas
      setUploadedImages({
        original: result.original,
        avatar: result.thumbnail
      });
      
      // Si se sube una nueva imagen, resetear el estado de eliminación
      setPhotoDeleted(false);
      
    } catch (error) {
      snackbar(t('common.errorUploadingImages') + (error.message || error));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAvatarClick = (event) => {
    // Solo permitir cambiar imagen si estamos editando una veterinaria existente
    if (isEditing(vet)) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCameraCapture = () => {
    handleMenuClose();
    setShowInlineCamera(true);
  };

  const handleGallerySelect = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemovePhoto = () => {
    handleMenuClose();
    setPhotoDeleted(true);
    setCapturedPhoto(null);
    setUploadedImages({ original: null, avatar: null });
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    setCapturedPhoto({
      url: previewUrl,
      file: file,
      isImage: true
    });

    setPhotoDeleted(false);
    await handlePhotoAndThumbUpload(file);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const unique_id = uuidv4();
      const extension = file.name.split('.').pop();
      const newFileName = `${unique_id}.${extension}`;
      
      const renamedFile = new File([file], newFileName, { type: file.type });
      
      const processedPhoto = {
        file: renamedFile,
        url: URL.createObjectURL(file),
        name: newFileName,
        size: file.size,
        type: file.type,
        isImage: true,
        isVideo: false,
        sizeInMB: (file.size / 1024 / 1024).toFixed(2)
      };
      
      setCapturedPhoto(processedPhoto);
      setPhotoDeleted(false);
      await handlePhotoAndThumbUpload(renamedFile);
    }
    
    event.target.value = '';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateVet({
      [name]: type === 'checkbox' ? checked : value,
    });
  }

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleSubmit = async() => {
    if (isEditing(vet)){
      update();
      return;
    }
    
    if (isBranch) {
      save_branch();
      return;
    }
      
    save();
  }

  const save = async () => {

    if (!validateNonEmpty('name', vet.name, setErrors, refs.name) ||
        !validateNonEmpty('region', vet.region?.friendly_name, setErrors, refs.region) || 
        !validateEmail('email', vet.email, setErrors, refs.email))
      return;

    setIsLoading(true);

    // No incluir imágenes durante la creación inicial (flujo de inicialización)
    vet.pre_access_token = preAccessToken;

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}vets?create_vet`,
        vet
      );
      const response2 = await axios.post(
        `${import.meta.env.VITE_API_URL}users?email_auth_web`,
        { vet: { id: response.data.data.id }, pre_access_token: preAccessToken },
        { withCredentials: true }
      );
      //console.log(response2);

      login();
      reloadConfig();
      navigate('/main', {}, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  }
  const save_branch = async () => {

    if (!validateNonEmpty('name', vet.name, setErrors, refs.name) ||
        !validateNonEmpty('region', vet.region?.friendly_name, setErrors, refs.region) || 
        !validateEmail('email', vet.email, setErrors, refs.email))
      return;

    setIsLoading(true);

    const vetToSend = { ...vet };

    // Add resources array if images were uploaded
    vetToSend.resources = [];
    if (uploadedImages.original && uploadedImages.avatar) {
      vetToSend.resources = [
        {
          file_name: uploadedImages.original.file_name,
          table_name: "vets",
          type: "PUBLIC",
          unique_id: uploadedImages.original.unique_id
        },
        {
          file_name: uploadedImages.avatar.file_name,
          table_name: "vets", 
          type: "PUBLIC",
          unique_id: uploadedImages.avatar.unique_id
        }
      ];
    }

    // Mark photo as deleted if user removed it
    if (photoDeleted) {
      vetToSend.photo_deleted = 1;
    }

    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}vets?create_branch`,
        removeNullsAndEmptys(vetToSend),
        { withCredentials: true }
      );

      // Reload config to update vet information
      await reloadConfig();

      navigate('/main/view-vet', {}, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  }
  const update = async () => {
    if (!validateNonEmpty('name', vet.name, setErrors, refs.name) ||
        !validateNonEmpty('region', vet.region?.friendly_name, setErrors, refs.region))
      return;

    setIsLoading(true);

    const vetToSend = { ...vet };

    // Add resources array if images were uploaded
    vetToSend.resources = [];
    if (uploadedImages.original && uploadedImages.avatar) {
      vetToSend.resources = [
        {
          file_name: uploadedImages.original.file_name,
          table_name: "vets",
          type: "PUBLIC",
          unique_id: uploadedImages.original.unique_id
        },
        {
          file_name: uploadedImages.avatar.file_name,
          table_name: "vets", 
          type: "PUBLIC",
          unique_id: uploadedImages.avatar.unique_id
        }
      ];
    }

    // Mark photo as deleted if user removed it
    if (photoDeleted) {
      vetToSend.photo_deleted = 1;
    }

    //console.log(vetToSend);
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}vets?id=${vet.id}`,
        removeNullsAndEmptys(vetToSend),
        { withCredentials: true }
      );
      
      // Reload config to update vet information
      await reloadConfig();
      
      navigate('/main/view-vet', {}, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container>

      {/* Barra Superior */}
      <AppBar position="static" color="primary" sx={{ mt: 4 }}>
        <Toolbar>
          {/* Botón de flecha hacia atrás */}
          {/* <IconButton color="inherit" onClick={handleBackClick} edge="start" sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton> */}
          {/* Icono */}
          {!isInitCreate && (
            <Avatar 
              sx={{ 
                bgcolor: 'gray.500',
                ...(isEditing(vet) && {
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                })
              }}
              src={isEditing(vet) && !photoDeleted ? (capturedPhoto?.url || vet?.thumb_url || '') : ''}
              onClick={handleAvatarClick}
            >
              {(!isEditing(vet) || (!capturedPhoto && !vet?.thumb_url) || photoDeleted) && <StoreIcon />}
            </Avatar>
          )}

          {/* Contenedor para el texto */}
          <Box sx={{ ml: 2 }}>
            <Typography variant="h6" component="div">
              {isEditing(vet) ? t('edit.editVet') : t('edit.newVet')}
            </Typography>
            <Typography variant="subtitle2" component="div">
              {t('common.completeData')}
            </Typography>
          </Box>

          {/* Espaciador flexible para empujar el botón de cierre hacia la derecha */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Botón de cierre (X) en el extremo derecho */}
          <IconButton color="inherit" onClick={handleBackClick} edge="end">
            <CloseIcon />
          </IconButton>

        </Toolbar>
      </AppBar>

      {/* Menú de opciones de imagen (solo en modo edición) */}
      {isEditing(vet) && (
        <>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleCameraCapture}>
              <ListItemIcon>
                <CameraAltIcon />
              </ListItemIcon>
              <ListItemText>{t('common.camera')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleGallerySelect}>
              <ListItemIcon>
                <PhotoLibraryIcon />
              </ListItemIcon>
              <ListItemText>{t('common.gallery')}</ListItemText>
            </MenuItem>
            {(capturedPhoto || vet.photo_url || vet.thumb_url) && !photoDeleted && (
              <MenuItem onClick={handleRemovePhoto}>
                <ListItemIcon>
                  <DeleteIcon />
                </ListItemIcon>
                <ListItemText>{t('common.remove')}</ListItemText>
              </MenuItem>
            )}
          </Menu>

          {/* Input file hidden para selección de galería */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileSelect}
          />

          <InlinePhotoCapture 
            open={showInlineCamera}
            onPhotoConfirm={handleInlinePhotoConfirm}
            onClose={() => setShowInlineCamera(false)}
          />
        </>
      )}

      {/* Contenido del formulario */}
      <Box sx={{ mt: 2, mb: 4 }}>
        {/* Campos de texto */}
        <TextField
          fullWidth
          label={t('common.nameStar')}
          name="name"
          value={vet.name}
          onChange={handleChange}
          margin="normal"
          inputRef={refs.name}
          error={!!errors.name}
          helperText={errors.name}
        />

        <TextField
          fullWidth
          label={t('common.address')}
          name="address"
          value={vet.address}
          onChange={handleChange}
          margin="normal"
          multiline
        />

        {/* Región con Autocomplete */}
        <Autocomplete
          options={regions}
          getOptionLabel={(option) => (option ? option.friendly_name : "")}
          value={vet.region}
          onChange={(e, newValue) => {
            if (!newValue) {
              updateVet({ region: newValue });
              return;
            }
            fetchFiscalTypes_(newValue.country);
            const us = isUnitedStatesRegion(newValue);
            updateVet({
              region: newValue,
              unit_system: us ? 'ENGLISH' : 'METRIC',
              hour_format: us ? 'AM_PM' : '24_HS',
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('edit.regionStar')}
              name="region"
              margin="normal"
              inputRef={refs.region}
              error={!!errors.region}
              helperText={errors.region}
            />
          )}
        />

        <TextField
          fullWidth
          label={t('common.phone')}
          name="phone"
          value={vet.phone}
          onChange={handleChange}
          margin="normal"
        />

        {!isEditing(vet) && (
          <TextField
            fullWidth
            label={t('edit.emailStar')}
            name="email"
            value={vet.email}
            onChange={handleChange}
            margin="normal"
            inputRef={refs.email}
            error={!!errors.email}
            helperText={errors.email}
          />
        )}

        <TextField
          fullWidth
          label={t('common.webPage')}
          name="web_page"
          value={vet.web_page}
          onChange={handleChange}
          margin="normal"
        />

        <TextField
          fullWidth
          label={t('common.notes')}
          name="notes"
          value={vet.notes}
          onChange={handleChange}
          margin="normal"
          multiline
        />

        {/* Divisor */}
        <Divider sx={{ my: 4 }} />

        {/* Información adicional */}
        <Typography variant="body1" gutterBottom>
          {t('edit.optionalConfig')}
        </Typography>

        {/* Nombramiento de clientes */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="owner-naming-label">{t('edit.clientNaming')}</InputLabel>
          <Select
            labelId="owner-naming-label"
            label={t('edit.clientNaming')}
            name="owner_naming"
            value={vet.owner_naming}
            onChange={handleChange}
          >
            {ownerNamingOptions.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Nombramiento de pacientes */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="pet-naming-label">{t('edit.patientNaming')}</InputLabel>
          <Select
            labelId="pet-naming-label"
            label={t('edit.patientNaming')}
            name="pet_naming"
            value={vet.pet_naming}
            onChange={handleChange}
          >
            {petNamingOptions.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sistema de medida */}
        <FormControl component="fieldset" margin="normal">
          <FormLabel component="legend">{t('edit.measurementSystem')}</FormLabel>
          <RadioGroup
            row
            name="unit_system"
            value={vet.unit_system}
            onChange={handleChange}
          >
            <FormControlLabel
              value="METRIC"
              control={<Radio />}
              label={t('edit.metricDecimal')}
            />
            <FormControlLabel
              value="ENGLISH"
              control={<Radio />}
              label={t('edit.imperial')}
            />
          </RadioGroup>
        </FormControl>

        {/* Formato horario */}
        <FormControl component="fieldset" margin="normal">
          <FormLabel component="legend">{t('edit.timeFormat')}</FormLabel>
          <RadioGroup
            row
            name="hour_format"
            value={vet.hour_format}
            onChange={handleChange}
          >
            <FormControlLabel
              value="24_HS"
              control={<Radio />}
              label={t('edit.hours24')}
            />
            <FormControlLabel
              value="AM_PM"
              control={<Radio />}
              label={t('edit.amPm')}
            />
          </RadioGroup>
        </FormControl>

        {/* Checkboxes */}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={vet.mobile_services}
                onChange={handleChange}
                name="mobile_services"
              />
            }
            label={t('edit.mobileServices')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={vet.email_messaging}
                onChange={handleChange}
                name="email_messaging"
              />
            }
            label={t('edit.emailMessaging')}
          />
        </FormGroup>

        {/* Divisor */}
        <Divider sx={{ my: 4 }} />

        {/* Ventas */}
        <Typography variant="subtitle1" gutterBottom>
          {t('edit.salesSection')}
        </Typography>

        {/* Checkboxes de ventas */}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={vet.sells_planning_activity}
                onChange={handleChange}
                name="sells_planning_activity"
              />
            }
            label={t('edit.showNextSupply')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={vet.sells_save_p1}
                onChange={handleChange}
                name="sells_save_p1"
              />
            }
            label={t('edit.overwritePrice')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={vet.sells_accept_suggested}
                onChange={handleChange}
                name="sells_accept_suggested"
              />
            }
            label={t('edit.acceptSuggestions')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={vet.sells_lock_price}
                onChange={handleChange}
                name="sells_lock_price"
              />
            }
            label={t('edit.blockPriceChange')}
          />
        </FormGroup>

        {/* Punto de venta por defecto */}
        {isEditing(vet) && (
          <FormControl fullWidth margin="normal">
            <InputLabel id="default-sell-point-label">{t('edit.defaultSellPoint')}</InputLabel>
            <Select
              labelId="default-sell-point-label"
              label={t('edit.defaultSellPoint')}
              name="default_sell_point"
              value={vet.default_sell_point ? vet.default_sell_point : ''}
              onChange={handleChange}
            >
              {sellPoints.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Divisor */}
        <Divider sx={{ my: 4 }} />

        {/* Fiscal */}
        <Typography variant="subtitle1" gutterBottom>
          {t('edit.fiscalSection')}
        </Typography>

        <TextField
          fullWidth
          label={t('edit.fiscalId')}
          name="fiscal_id"
          value={vet.fiscal_id}
          onChange={handleChange}
          margin="normal"
        />

        <Autocomplete
          options={fiscalTypes}
          getOptionLabel={(option) => (option ? option.name : "")}
          value={vet.fiscal_type}
          onChange={(event, newValue) => {
            updateVet({ fiscal_type: newValue });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.fiscalType')}
              name="fiscal_type"
              margin="normal"
            />
          )}
        />

        {/* Floating Action Button */}
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Fab 
            color="primary" 
            aria-label={t('common.save')} 
            onClick={handleSubmit}
            disabled={isLoading || isUploadingImage}
          >
            <CheckIcon />
          </Fab>
        </Box>

        {(isLoading || isUploadingImage) && (
          <CircularProgress
            size={42}
            sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
          />
        )}

      </Box>

    </Container>
  );
};

export default EditVet;
